import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import AppShell, { storedSession } from '../components/AppShell';
import ChainStepper from '../components/workflows/ChainStepper';
import JobRecruiterTabs, { JD_SECTIONS, SectionView } from '../components/JobRecruiterTabs';
import ScreeningQuestionCard from '../components/ScreeningQuestionCard';
import { WeightSuggestions } from '../components/candidates/MatchPanel';
import { UnlinkedInterviews } from '../components/candidates/AlexDialogs';
import InterviewPlanEditor from '../components/jobs/InterviewPlanEditor';
import { JobDialog } from '../components/jobs/JobDialogs';
import LoadError, { isServerError } from '../components/jobs/LoadError';
import { AgeingPill, FunnelBar, FunnelHead, StatusPill } from '../components/jobs/Funnel';
import { api, btnDanger, btnPrimary, btnSecondary, card, EmptyState, InlineError, input, labelCls, Skeleton, Tabs, timeAgo, ToastProvider, useToast } from '../components/ui/Kit';
import { formatDate } from '../utils/dates';
import { downloadJob } from '../utils/jobExport';

// /jobs/:id — the job page for every role (bundle 3a-2). An HM's own draft opens the form / call / review flow.
const TAB_LABEL = { overview: 'Overview', jd: 'Job description', questions: 'Screening questions', brief: 'Recruiter brief', plan: 'Interview plan', activity: 'Activity' };

function Banner({ tone, title, children, testid }) {
  const tones = { amber: 'bg-[#FFFBEB] border-[#FDE68A] text-[#78350F]', red: 'bg-[#FEF2F2] border-[#FECACA] text-[#7F1D1D]',
    violet: 'bg-[#F5F3FF] border-[#DDD6FE] text-[#4C1D95]', gray: 'bg-[#F8FAFC] border-[#E2E8F0] text-[#334155]' };
  return <div role="status" data-testid={testid} className={`border rounded-[10px] px-4 py-3 text-sm ${tones[tone]}`}><p className="font-bold">{title}</p>{children}</div>;
}

function Banners({ b, job, navigate }) {
  const when = (iso) => (iso ? formatDate(iso) : '');
  return (
    <div className="space-y-2">
      {b.change_pending && (
        <Banner tone="amber" title="Change waiting for approval" testid="banner-change">
          <p className="mt-0.5">Waiting on {b.change_pending.waiting_on}{b.change_pending.requested_by ? ` · proposed by ${b.change_pending.requested_by}` : ''}. The job stays live with its current values until approved.</p>
          <table className="mt-2 text-[13px]"><tbody>
            {b.change_pending.fields.map((x) => (
              <tr key={x.key}><td className="pr-3 font-semibold">{x.label}</td><td className="pr-3 line-through opacity-70">{x.current || '—'}</td><td>→ {x.proposed}</td></tr>
            ))}</tbody></table>
        </Banner>
      )}
      {b.change_sent_back && (
        <Banner tone="red" title="Change sent back" testid="banner-change-sent-back">
          <p className="mt-0.5">{b.change_sent_back.by ? `${b.change_sent_back.by}: ` : ''}{b.change_sent_back.comment} — the job keeps its approved title and pay.</p>
        </Banner>
      )}
      {b.sent_back && (
        <Banner tone="red" title="Sent back to the hiring manager" testid="banner-sent-back">
          <p className="mt-0.5">{b.sent_back.by ? `${b.sent_back.by}: ` : ''}{b.sent_back.comment}</p>
          {job.intake_id && job.actions.edit && <button className="mt-1 font-semibold underline" onClick={() => navigate(`/jobs/intake/${job.intake_id}/review`)}>Edit and resubmit</button>}
        </Banner>
      )}
      {b.on_hold && <Banner tone="violet" title={`On hold since ${when(b.on_hold.since)}${b.on_hold.by ? ` by ${b.on_hold.by}` : ''}`} testid="banner-hold">{b.on_hold.note && <p className="mt-0.5">{b.on_hold.note}</p>}</Banner>}
      {b.closed && <Banner tone="gray" title={`Closed — ${b.closed.reason}`} testid="banner-closed"><p className="mt-0.5">{when(b.closed.at)}{b.closed.by ? ` by ${b.closed.by}` : ''}{b.closed.note ? ` · ${b.closed.note}` : ''}</p></Banner>}
    </div>
  );
}

function Approvals({ steps, onDone }) {
  const toast = useToast();
  const [comment, setComment] = useState({});
  const [error, setError] = useState('');
  if (!steps.length) return <p className="text-sm text-[#64748B]">No approval steps for the current version.</p>;
  const decide = async (s, decision) => {
    setError('');
    try { await api(`/api/job-routing/approvals/${s.id}/decide`, { method: 'POST', body: { decision, comment: comment[s.id] || '' } }); toast.show(decision === 'approve' ? 'Approved' : 'Sent back'); onDone(); }
    catch (e) { setError(e.message); }
  };
  return (
    <div className="space-y-2"><InlineError>{error}</InlineError>
    <ul className="space-y-2" data-testid="approvals">
      {steps.map((s) => (
        <li key={s.id} className="border border-[#E6E9F0] rounded-lg p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2"><b>{s.label}</b>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.status === 'approved' ? 'bg-[#DCFCE7] text-[#166534]' : s.status === 'pending' ? 'bg-[#FEF3C7] text-[#92400E]' : 'bg-[#FEE2E2] text-[#991B1B]'}`}>{s.status}</span>
            {s.self_approved && <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FEF3C7] text-[#92400E]">self-approved — no other approver</span>}
          </div>
          <p className="text-xs text-[#64748B] mt-0.5">{s.status === 'pending' ? `Waiting on ${s.approver}` : `${s.decided_by || s.approver}${s.decided_at ? ` · ${formatDate(s.decided_at)}` : ''}`}{s.comment ? ` · “${s.comment}”` : ''}</p>
          {s.can_decide && (
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <input aria-label={`Comment for ${s.label}`} className={input + ' !w-72'} placeholder="Comment (needed to send back)" value={comment[s.id] || ''} onChange={(e) => setComment({ ...comment, [s.id]: e.target.value })} />
              <button className={btnPrimary} onClick={() => decide(s, 'approve')}>Approve</button>
              <button className={btnSecondary + ' !text-[#B91C1C]'} onClick={() => decide(s, 'reject')}>Send back</button>
            </div>
          )}
        </li>
      ))}
    </ul></div>
  );
}

function JobChain({ jobId, stamp }) {
  const [chain, setChain] = useState(null);
  const { user } = storedSession();
  useEffect(() => { api(`/api/jobs/${jobId}/chain`).then(setChain).catch(() => setChain({ steps: [] })); }, [jobId, stamp]);
  if (!chain) return <Skeleton className="h-24 w-full" />;
  return (
    <div>
      {chain.workflow && <p className="text-xs text-[#64748B] mb-2">{chain.workflow.name} · version {chain.workflow.version_no}{chain.kind === 'change' ? ' · title / pay change' : ''}</p>}
      <ChainStepper chain={chain} me={user.full_name} />
    </div>
  );
}

function Flags({ job, canEdit, onDone }) {
  const toast = useToast();
  const [target, setTarget] = useState(String(job.target_days));
  const [error, setError] = useState('');
  const patch = async (body) => {
    setError('');
    try { await api(`/api/jobs/${job.id}`, { method: 'PATCH', body }); toast.show('Saved'); onDone(); } catch (e) { setError(e.message); }
  };
  return (
    <div className="space-y-2 text-sm" data-testid="flags">
      {[['bgv_required', 'Background check required'], ['drug_test_required', 'Drug test required']].map(([k, l]) => (
        <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={!!job[k]} disabled={!canEdit} onChange={(e) => patch({ [k]: e.target.checked })} />{l}</label>
      ))}
      <div className="flex items-end gap-2">
        <label className="block"><span className={labelCls}>Target days to fill</span>
          <input className={input + ' !w-24'} inputMode="numeric" value={target} disabled={!canEdit} onChange={(e) => setTarget(e.target.value)} /></label>
        {canEdit && <button className={btnSecondary} disabled={String(job.target_days) === target} onClick={() => patch({ target_days: Number(target) })}>Save</button>}
      </div>
      <InlineError>{error}</InlineError>
    </div>
  );
}

function Activity({ jobId }) {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api(`/api/jobs/${jobId}/timeline`).then((r) => setEvents(r.events)).catch((e) => setError(e.message)); }, [jobId]);
  if (error) return <InlineError>{error}</InlineError>;
  if (!events) return <Skeleton className="h-24 w-full" />;
  if (!events.length) return <EmptyState title="No activity yet" />;
  return (
    <ol className="relative border-l border-[#E6E9F0] ml-2" data-testid="timeline">
      {events.map((e) => (
        <li key={e.id} className="ml-4 pb-4">
          <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full bg-[#0066CC]" aria-hidden="true" />
          <p className="text-sm font-semibold text-[#0F172A]">{e.text}</p>
          <p className="text-xs text-[#64748B]">{e.by || 'Quali5d'} · {timeAgo(e.at)} · {formatDate(e.at)}</p>
          {e.comment && <p className="text-sm text-[#334155] mt-0.5">“{e.comment}”</p>}
        </li>
      ))}
    </ol>
  );
}

function Header({ data, onDialog, navigate }) {
  const j = data.job;
  const a = j.actions || {};
  const recs = j.recruiters || [];
  const btn = (kind, label, cls = btnSecondary) => <button key={kind} className={cls} onClick={() => onDialog(kind, j)}>{label}</button>;
  return (
    <div className={card + ' p-5'}>
      <button className="text-sm text-[#0066CC] font-semibold mb-2" onClick={() => navigate('/jobs')}>← Jobs</button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2"><h1 className="text-2xl font-extrabold text-[#0F172A]">{j.job_title}</h1><StatusPill status={j.status} sentBack={j.sent_back} /></div>
          <p className="text-sm text-[#64748B] mt-0.5">{[j.req_id, j.department, j.openings ? `${j.openings} opening${j.openings > 1 ? 's' : ''}` : null, j.location, j.work_mode].filter(Boolean).join(' · ')}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm">
            <span><span className="text-[#64748B]">Hiring manager </span><b>{j.hiring_manager.full_name || '—'}</b></span>
            <span><span className="text-[#64748B]">Recruiters </span>{recs.length ? recs.map((r) => (
              <b key={r.user_id} className="mr-2">{r.full_name}{r.is_primary && <span className="ml-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#E8F1FB] text-[#003D82]">Primary</span>}</b>)) : <span className="text-[#94A3B8]">not assigned</span>}</span>
            <span><span className="text-[#64748B]">Days open </span><b>{j.days_open} / {j.target_days}</b> <AgeingPill days={j.days_open} tone={j.ageing} /></span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="job-actions">
          {a.search && <button className={btnPrimary} onClick={() => navigate(`/jobs/${j.id}/candidates`)} data-testid="find-candidates">Find candidates</button>}
          {a.change && btn('change', 'Change title or pay')}
          {a.edit && j.intake_id && j.origin === 'hm_jessica' && j.status !== 'draft' && <button className={btnSecondary} onClick={() => navigate(`/jobs/intake/${j.intake_id}/review`)}>Edit JD / questions</button>}
          {a.assign && btn('assign', 'Assign', btnPrimary)}
          {a.hand_off && btn('hand_off', 'Hand to lead')}
          {a.recruit_myself && btn('myself', 'Recruit myself')}
          {a.reassign && btn('reassign', 'Reassign')}
          {a.hold && btn('hold', 'Put on hold')}
          {a.resume && btn('resume', 'Take off hold', btnPrimary)}
          {a.close && btn('close', 'Close job')}
          {a.reopen && btn('reopen', 'Reopen', btnPrimary)}
          {a.delete && btn('delete', 'Delete', btnDanger)}
        </div>
      </div>
    </div>
  );
}

function Content({ tab, data, onDialog, reload }) {
  const navigate = useNavigate();
  const toast = useToast();
  const j = data.job;
  const c = data.content;
  if (tab === 'overview') {
    return (
      <div className="grid xl:grid-cols-[1fr_340px] gap-5">
        <div className="space-y-5">
          <section className={card + ' p-5'}><div className="flex flex-wrap items-center justify-between gap-2 mb-3"><h2 className="text-base font-extrabold">Pipeline</h2>
            {(j.actions || {}).search && <button className={btnSecondary + ' text-xs py-1.5'} onClick={() => onDialog('compare', j)} data-testid="compare-open">Compare finalists</button>}</div>
            <div className="overflow-x-auto"><FunnelHead /><div className="mt-1"><FunnelBar stages={j.stages} onOpen={(column, o) => onDialog('stage', j, { column, ...o })} /></div></div>
            <p className="text-xs text-[#64748B] mt-3">One place per candidate · click a number for the people behind it.</p></section>
          <section className={card + ' p-5'}><h2 className="text-base font-extrabold mb-3">Approval chain</h2>
            <JobChain jobId={j.id} stamp={data.approvals.map((x) => `${x.id}.${x.status}`).join(',')} />
            {data.approvals.some((x) => x.can_decide) && <div className="mt-4 border-t border-[#EEF1F6] pt-4"><p className="text-sm font-bold mb-2">Your decision</p>
              <Approvals steps={data.approvals.filter((x) => x.can_decide)} onDone={reload} /></div>}</section>
          {c.kind !== 'legacy' && <WeightSuggestions jobId={j.id} />}
          {data.job.actions && data.job.actions.search && <UnlinkedInterviews jobId={j.id} hideWhenEmpty />}
        </div>
        <section className={card + ' p-5'}><h2 className="text-base font-extrabold mb-3">Job settings</h2><Flags job={j} canEdit={data.can_edit_flags} onDone={reload} />
          {c.kind === 'legacy' && c.quality_score != null && <p className="text-sm mt-4">Quality score <b>{c.quality_score}</b></p>}
          {c.kind === 'legacy' && <button className="text-sm text-[#0066CC] font-semibold mt-3" onClick={() => navigate(`/jobs/${j.id}/classic`)}>Classic view (all original tabs) →</button>}
        </section>
      </div>
    );
  }
  if (tab === 'jd') {
    if (c.kind === 'legacy') return <section className={card + ' p-6 prose max-w-none'}>{c.jd_text ? <ReactMarkdown>{c.jd_text}</ReactMarkdown> : <EmptyState title="No job description stored for this job" />}</section>;
    const jd = c.jd || {};
    const dl = async (format) => {
      try { await downloadJob(format, { intakeId: j.intake_id, reqId: j.req_id, jobTitle: j.job_title, versionNo: c.version_no, jd, questions: c.screening_questions }); }
      catch (e) { toast.show(e.message, 'error'); }
    };
    return (
      <section className={card + ' p-6'} data-testid="tab-jd">
        <div className="flex flex-wrap justify-between gap-2 mb-4"><p className="text-xs text-[#64748B]">Version {c.version_no}</p>
          <div className="flex gap-2"><button className={btnSecondary} onClick={() => dl('pdf')}>Download PDF</button><button className={btnSecondary} onClick={() => dl('docx')}>Download Word</button></div></div>
        {JD_SECTIONS.map((s) => (jd[s.key] && (Array.isArray(jd[s.key]) ? jd[s.key].length > 0 : true)) && (
          <div key={s.key} className="mb-6"><h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-2">{s.label}</h3><SectionView section={s} value={jd[s.key]} /></div>
        ))}
      </section>
    );
  }
  if (tab === 'questions') {
    if (c.kind === 'legacy') {
      return (
        <section className={card + ' p-6'}>
          {data.tabs.includes('brief') && <button className={btnSecondary + ' mb-3'} onClick={() => navigate(`/edit-screening/${j.id}`)}>Edit questions</button>}
          {c.screening_questions_text ? <div className="prose max-w-none"><ReactMarkdown>{c.screening_questions_text}</ReactMarkdown></div> : <EmptyState title="No screening questions" />}
        </section>
      );
    }
    const qs = c.screening_questions || [];
    return (
      <section className="space-y-3" data-testid="tab-questions">
        {!qs.length && <EmptyState title="No screening questions yet" />}
        {qs.map((q, i) => <ScreeningQuestionCard key={`${i}.${q.question}`} q={q} index={i} total={qs.length} />)}
      </section>
    );
  }
  if (tab === 'plan') return <InterviewPlanEditor jobId={j.id} />;           // 5a
  if (tab === 'brief') {
    if (c.kind === 'legacy') return <section className={card + ' p-6 prose max-w-none'}>{c.recruiter_document ? <ReactMarkdown>{c.recruiter_document}</ReactMarkdown> : <EmptyState title="No recruiter guide for this job" />}</section>;
    return (
      <div className="space-y-5">
        <section className={card}><JobRecruiterTabs tab="brief" view={{ recruiter_brief: c.recruiter_brief || {} }} /></section>
        {c.fields && <section className={card + ' p-5'}><h2 className="text-base font-extrabold mb-3">Hiring manager's answers</h2>
          <dl className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">{c.fields.map((x) => <div key={x.no}><dt className="text-xs text-[#64748B]">{x.no}. {x.label}</dt><dd>{x.display_text || '—'}</dd></div>)}</dl></section>}
        {c.transcript && <section className={card + ' p-5'}><h2 className="text-base font-extrabold mb-3">Jessica call transcript</h2><pre className="whitespace-pre-wrap text-sm text-[#334155]">{c.transcript}</pre></section>}
      </div>
    );
  }
  return <section className={card + ' p-5'}><Activity jobId={j.id} /></section>;
}

function Inner() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const initialTab = new URLSearchParams(window.location.search).get('tab');
  const [tab, setTab] = useState(initialTab || 'overview');
  const load = useCallback(async () => {
    setError(''); setLoadError(null);
    try {
      const d = await api(`/api/jobs/${id}`);
      if (d.redirect_to) { navigate(d.redirect_to, { replace: true }); return; }
      setData(d);
    } catch (e) {
      if (e.status === 404) setError('This job isn’t available to you — it may have been deleted or moved.');
      else if (isServerError(e)) setLoadError(e);
      else setError(e.message);
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  const tabs = data ? (data.job.status === 'draft' || data.tabs.includes('plan') ? data.tabs
    : [...data.tabs.filter((t) => t !== 'activity'), 'plan', ...data.tabs.filter((t) => t === 'activity')]) : [];   // 5a: the interview plan
  const shown = tabs.includes(tab) ? tab : 'overview';
  const change = (t) => { setTab(t); window.history.replaceState(null, '', `/jobs/${id}${t === 'overview' ? '' : `?tab=${t}`}`); };
  return (
    <AppShell active="jobs">
      {loadError ? <LoadError error={loadError} what="this job" onRetry={() => { setLoadError(null); setData(null); load(); }} />
        : error ? <div className={card + ' p-6'}><InlineError>{error}</InlineError><button className={btnSecondary + ' mt-3'} onClick={() => navigate('/jobs')}>Back to jobs</button></div>
        : !data ? <div className="space-y-3" aria-busy="true"><Skeleton className="h-36 w-full" /><Skeleton className="h-64 w-full" /></div> : (
          <div className="space-y-4" data-testid="job-page">
            <Header data={data} onDialog={(kind, job, extra = {}) => setDialog({ kind, job, ...extra })} navigate={navigate} />
            {data.data_issue && <Banner tone="amber" title="Some details of this job couldn’t be loaded" testid="banner-data-issue"><p className="mt-0.5">What’s shown is safe to use; missing parts appear empty. The problem has been logged.</p></Banner>}
            <Banners b={data.banners} job={data.job} navigate={navigate} />
            <Tabs tabs={tabs.map((k) => ({ key: k, label: TAB_LABEL[k] }))} active={shown} onChange={change} label="Job sections" />
            <div role="tabpanel" id={`panel-${shown}`} aria-labelledby={`tab-${shown}`}>
              <Content tab={shown} data={data} onDialog={(kind, job, extra = {}) => setDialog({ kind, job, ...extra })} reload={load} />
            </div>
          </div>
        )}
      <JobDialog dialog={dialog} onClose={() => setDialog(null)} onDone={(out) => {
        setDialog(null);
        if (dialog && dialog.kind === 'delete') navigate('/jobs'); else load();
        return out;
      }} />
    </AppShell>
  );
}

export default function JobPage() {
  return <ToastProvider><Inner /></ToastProvider>;
}
